"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParsedCallStack = exports.ParsedCallStackLine = void 0;
exports.parseCallStack = parseCallStack;
class ParsedCallStackLine {
    constructor(raw, methodName, options = {}) {
        this.raw = raw;
        this.methodName = methodName;
        this.lineNumber = options.lineNumber;
        this.arguments = options.arguments;
        this.file = options.file;
        this.column = options.column;
        this.isAnonymous = options.isAnonymous ?? false;
    }
    get hasFileLocation() {
        return Boolean(this.file);
    }
    async inNodeModulesOrSystem(fs, path) {
        if (!this.file) {
            return false;
        }
        if (this.file.startsWith('node:')) {
            return true;
        }
        if (this._inNodeModules === undefined) {
            let realPath;
            try {
                realPath = await fs.realpath(this.file);
            }
            catch {
                realPath = this.file;
            }
            const pathSegments = realPath.split(path.sep);
            this._inNodeModules = pathSegments.includes('node_modules');
        }
        return this._inNodeModules;
    }
    static fromStackLine(line) {
        const cleanLine = line.replace(/^\s*at\s+/, '');
        const isAnonymous = cleanLine.includes('<anonymous>');
        const filePathMatch = cleanLine.match(/^(.+?)\s+\((.+?):(\d+):(\d+)\)$/);
        if (filePathMatch) {
            const [, methodPart, file, lineStr, columnStr] = filePathMatch;
            return new ParsedCallStackLine(line, methodPart.trim(), {
                file,
                lineNumber: parseInt(lineStr, 10),
                column: parseInt(columnStr, 10),
                isAnonymous,
            });
        }
        const directFileMatch = cleanLine.match(/^(.+?):(\d+):(\d+)$/);
        if (directFileMatch) {
            const [, file, lineStr, columnStr] = directFileMatch;
            return new ParsedCallStackLine(line, '<unknown>', {
                file,
                lineNumber: parseInt(lineStr, 10),
                column: parseInt(columnStr, 10),
                isAnonymous,
            });
        }
        const anonymousMatch = cleanLine.match(/^(.+?)\s+\(<anonymous>\)$/);
        if (anonymousMatch) {
            const [, methodName] = anonymousMatch;
            return new ParsedCallStackLine(line, methodName.trim(), {
                isAnonymous: true,
            });
        }
        const methodMatch = cleanLine.match(/^(.+?)(\(.*\))?$/);
        if (methodMatch) {
            const [, methodPart, argsPart] = methodMatch;
            return new ParsedCallStackLine(line, methodPart.trim(), {
                arguments: argsPart?.trim(),
                isAnonymous,
            });
        }
        return new ParsedCallStackLine(line, cleanLine, {
            isAnonymous,
        });
    }
    toString() {
        return this.raw;
    }
}
exports.ParsedCallStackLine = ParsedCallStackLine;
class ParsedCallStack {
    constructor(stackLines) {
        this.stackLines = stackLines;
    }
    async hasLineNotInNodeModules(fs, path, { ignorePaths = [], skipLines = 0 } = {}) {
        let lineNo = 0;
        for (const line of this.stackLines) {
            if (lineNo++ < skipLines) {
                continue;
            }
            if (!line.hasFileLocation) {
                continue;
            }
            if (line.file && ignorePaths.some((p) => line.file?.includes(p))) {
                continue;
            }
            if (!(await line.inNodeModulesOrSystem(fs, path))) {
                return true;
            }
        }
        return false;
    }
    async getLinesNotInNodeModulesOrSystem(fs, path) {
        return Promise.all(this.stackLines.map((line) => line.inNodeModulesOrSystem(fs, path))).then((inNodeModules) => this.stackLines.filter((line, index) => !line.hasFileLocation || !inNodeModules[index]));
    }
    getLines() {
        return this.stackLines;
    }
    static fromStackTrace(stackTrace) {
        const lines = stackTrace.split('\n');
        const stackLines = [];
        for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine || !trimmedLine.startsWith('at ')) {
                continue;
            }
            const parsedLine = ParsedCallStackLine.fromStackLine(trimmedLine);
            if (parsedLine) {
                stackLines.push(parsedLine);
            }
        }
        return new ParsedCallStack(stackLines);
    }
}
exports.ParsedCallStack = ParsedCallStack;
function parseCallStack(stackTrace) {
    return ParsedCallStack.fromStackTrace(stackTrace);
}
//# sourceMappingURL=parsed-call-stack.js.map